// src/pages/ServiceLogPage.js
// CALLOUT CHANGES: added OnboardingCallout import, exportBtnRef, callout at bottom of return.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import StoredImage from '../components/StoredImage';
import { TOOL_BUCKET } from '../lib/storedFile';
import { useAuth } from '../components/AuthContext';
import { isBranchScopedManager } from '../lib/permissions';
import { calcSavings, fmt$, fmtDate } from '../lib/utils';
import OnboardingCallout from '../components/OnboardingCallout';

function exportCSV(records) {
  const headers = [
    'Return Date', 'Tool', 'Branch', 'Shop', 'Ticket #',
    'Outcome', 'Warranty', 'Actual Cost', 'MSRP / Replacement MSRP', 'Savings',
    'Returned By', 'Resolution Notes', 'Tag Date'
  ];

  const rows = records.map(r => {
    const savings = calcSavings(r);
    const msrp = r.repair_or_replace === 'replaced'
      ? r.replacement_msrp
      : r.tools?.tool_models?.msrp;
    return [
      fmtDate(r.return_date),
      r.tools?.name || '',
      r.tools?.branches?.name || '',
      r.shop_name || '',
      r.ticket_number || '',
      r.repair_or_replace || '',
      r.is_warranty ? 'Yes' : 'No',
      r.actual_repair_cost != null ? Number(r.actual_repair_cost).toFixed(2) : '',
      msrp != null ? Number(msrp).toFixed(2) : '',
      savings != null ? Number(savings).toFixed(2) : '',
      r.returned_by_name || '',
      r.resolution_notes || '',
      fmtDate(r.tag_date),
    ];
  });

  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `service-log-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ServiceLogPage() {
  const { profile, isAdmin, isManager, isExecutive } = useAuth();
  const isBranchScoped = isBranchScopedManager({ isAdmin, isManager, isExecutive });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterOutcome, setFilterOutcome] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ── Callout ref ──
  const exportBtnRef = useRef(null);
  const isMobile = window.innerWidth <= 768;

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('tagout_log')
      .select('*, tools(name, model_number, serial_number, branches(id, name), tool_models(msrp))')
      .eq('status', 'Returned')
      .order('return_date', { ascending: false });

    if (isBranchScoped && profile?.branch_id) {
      query = query.eq('tools.branches.id', profile.branch_id);
    }

    const { data } = await query;
    setRecords(data || []);
    setLoading(false);
  }, [isBranchScoped, profile]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    supabase.from('branches').select('id, name').order('name').then(({ data }) => setBranches(data || []));
  }, []);

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || [r.tools?.name, r.shop_name, r.ticket_number, r.returned_by_name, r.resolution_notes]
      .filter(Boolean).join(' ').toLowerCase().includes(q);
    const matchBranch = !filterBranch || r.tools?.branches?.id === filterBranch;
    const matchOutcome = !filterOutcome
      ? true
      : filterOutcome === 'warranty'
        ? r.is_warranty
        : r.repair_or_replace === filterOutcome;
    const returnDate = r.return_date ? new Date(r.return_date) : null;
    const matchFrom = !dateFrom || (returnDate && returnDate >= new Date(dateFrom));
    const matchTo = !dateTo || (returnDate && returnDate <= new Date(dateTo + 'T23:59:59'));
    return matchSearch && matchBranch && matchOutcome && matchFrom && matchTo;
  });

  const totalCost = filtered.reduce((sum, r) => sum + (parseFloat(r.actual_repair_cost) || 0), 0);
  const totalSavings = filtered.reduce((sum, r) => {
    const s = calcSavings(r);
    return sum + (s != null ? s : 0);
  }, 0);
  const warrantyCount = filtered.filter(r => r.is_warranty).length;
  const replacedCount = filtered.filter(r => r.repair_or_replace === 'replaced').length;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div className="page-title">Service log</div>
            <div className="page-sub">All returned tools — repairs, replacements, and warranty claims</div>
          </div>
          {/* Export button — ref attached for callout anchor */}
          <button
            ref={exportBtnRef}
            className="btn btn-ghost btn-sm"
            onClick={() => exportCSV(filtered)}
            disabled={filtered.length === 0}
            style={{ marginTop: '4px' }}
          >
            ↓ Export CSV
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="stats-grid">
        <div className="stat-card green">
          <div className="stat-label">Records</div>
          <div className="stat-value green">{filtered.length}</div>
        </div>
        <div className="stat-card red">
          <div className="stat-label">Total cost</div>
          <div className="stat-value red" style={{ fontSize: filtered.length ? '20px' : undefined }}>{fmt$(totalCost)}</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Total savings</div>
          <div className="stat-value green" style={{ fontSize: filtered.length ? '20px' : undefined }}>{totalSavings > 0 ? fmt$(totalSavings) : '—'}</div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">Warranty claims</div>
          <div className="stat-value" style={{ color: '#1565C0' }}>{warrantyCount}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '8px' }}>
        <input
          className="search-input"
          placeholder="Search tool, shop, ticket #..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 180px' }}
        />
        {isAdmin && (
          <select className="filter-select" value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
            <option value="">All branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select className="filter-select" value={filterOutcome} onChange={e => setFilterOutcome(e.target.value)}>
          <option value="">All outcomes</option>
          <option value="repaired">Repaired</option>
          <option value="replaced">Replaced</option>
          <option value="warranty">Warranty</option>
        </select>
        <input
          type="date"
          className="filter-select"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          title="Return date from"
        />
        <input
          type="date"
          className="filter-select"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          title="Return date to"
        />
        {(search || filterBranch || filterOutcome || dateFrom || dateTo) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilterBranch(''); setFilterOutcome(''); setDateFrom(''); setDateTo(''); }}>
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" />Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">🔧</div>
            <div className="empty-state-title">No records found</div>
            <div className="empty-state-sub">No returned tools match your current filters.</div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.map(r => {
            const savings = calcSavings(r);
            const isReplaced = r.repair_or_replace === 'replaced';
            const receiptUrl = r.receipt_photo_url || null;   // #536 - the stored value; the address is fetched by StoredImage
            const msrp = isReplaced ? r.replacement_msrp : r.tools?.tool_models?.msrp;

            const outcomeColor = r.is_warranty ? '#1565C0' : isReplaced ? '#F5A623' : '#2E7D32';

            return (
              <div key={r.id} className="card" style={{ borderLeft: `4px solid ${outcomeColor}` }}>
                <div className="card-body">
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontFamily: 'var(--font-head)', fontSize: '15px', fontWeight: 700 }}>
                        {r.tools?.name || 'Unknown tool'}
                      </div>
                      <div className="text-sm text-muted">
                        {[r.tools?.branches?.name, r.tools?.model_number, r.tools?.serial_number ? 'S/N: ' + r.tools.serial_number : null].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {r.is_warranty && <span className="badge badge-transit">Warranty</span>}
                      <span className="badge" style={{
                        background: isReplaced ? '#FFF3E0' : '#E8F5E9',
                        color: isReplaced ? '#E65100' : '#2E7D32',
                        border: `1px solid ${isReplaced ? '#FFCC80' : '#A5D6A7'}`,
                        borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 600
                      }}>
                        {isReplaced ? 'Replaced' : 'Repaired'}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--gray-mid)', marginBottom: '6px' }}>
                    <span>Returned: <strong style={{ color: 'var(--black)' }}>{fmtDate(r.return_date)}</strong></span>
                    <span>Tagged: <strong style={{ color: 'var(--black)' }}>{fmtDate(r.tag_date)}</strong></span>
                    {r.shop_name && <span>Shop: <strong style={{ color: 'var(--black)' }}>{r.shop_name}</strong></span>}
                    {r.ticket_number && <span>Ticket #: <strong style={{ color: 'var(--black)' }}>{r.ticket_number}</strong></span>}
                    {r.returned_by_name && <span>By: <strong style={{ color: 'var(--black)' }}>{r.returned_by_name}</strong></span>}
                  </div>

                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--gray-mid)', marginBottom: '6px' }}>
                    <span>
                      Actual cost:{' '}
                      <strong style={{ color: r.is_warranty ? '#1565C0' : 'var(--black)' }}>
                        {r.is_warranty ? '$0.00 (warranty)' : fmt$(r.actual_repair_cost)}
                      </strong>
                    </span>
                    {msrp != null && (
                      <span>
                        {isReplaced ? 'Replacement MSRP' : 'Tool MSRP'}:{' '}
                        <strong style={{ color: 'var(--black)' }}>{fmt$(msrp)}</strong>
                      </span>
                    )}
                    {savings != null && (
                      <span>
                        Savings:{' '}
                        <strong style={{ color: savings > 0 ? '#2E7D32' : 'var(--gray-mid)' }}>
                          {savings > 0 ? fmt$(savings) : '—'}
                        </strong>
                      </span>
                    )}
                    {savings == null && (
                      <span style={{ fontStyle: 'italic' }}>Savings: <strong style={{ color: 'var(--gray-lt)' }}>— (no MSRP on file)</strong></span>
                    )}
                  </div>

                  {r.resolution_notes && (
                    <div style={{ fontSize: '12px', color: 'var(--green-dk)', fontStyle: 'italic', marginBottom: '6px' }}>
                      "{r.resolution_notes}"
                    </div>
                  )}

                  {receiptUrl && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--gray-mid)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Receipt / invoice</div>
                      <StoredImage
                        value={receiptUrl}
                        bucket={TOOL_BUCKET}
                        alt="Receipt"
                        style={{ maxWidth: '100%', maxHeight: '160px', borderRadius: '8px', objectFit: 'cover', cursor: 'pointer', border: '1px solid var(--border)' }}
                        openOnClick
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {filtered.length > 1 && (
            <div className="card" style={{ background: 'var(--off-white)' }}>
              <div className="card-body">
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '13px' }}>
                  <span style={{ color: 'var(--gray-mid)' }}>
                    {filtered.length} records &nbsp;·&nbsp;
                    <strong style={{ color: 'var(--black)' }}>{filtered.filter(r => r.repair_or_replace === 'repaired').length} repaired</strong>
                    {' · '}
                    <strong style={{ color: 'var(--black)' }}>{replacedCount} replaced</strong>
                    {' · '}
                    <strong style={{ color: '#1565C0' }}>{warrantyCount} warranty</strong>
                  </span>
                  <span style={{ color: 'var(--gray-mid)' }}>
                    Total cost: <strong style={{ color: 'var(--black)' }}>{fmt$(totalCost)}</strong>
                  </span>
                  {totalSavings > 0 && (
                    <span style={{ color: 'var(--gray-mid)' }}>
                      Total savings: <strong style={{ color: '#2E7D32' }}>{fmt$(totalSavings)}</strong>
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ONBOARDING CALLOUT ── */}
      <OnboardingCallout
        calloutKey="service_log_export"
        text="Export your complete service history to a spreadsheet — useful for monthly reporting or sharing repair costs with leadership."
        anchorRef={exportBtnRef}
        isMobile={isMobile}
        show={records.length > 0}
      />
    </div>
  );
}